/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { PrimaryButton, SearchInput, SecondaryButton } from "../../components";
import { useAlert } from "../../store/useAlert";
import { useAuth } from "../../store/useAuth";
import { useAppLoading } from "../../store/useAppLoading";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { Navigate, useSearchParams } from "react-router-dom";

interface MergeUser {
  _id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  balance?: number;
  droppies?: number;
  dropsVolume?: number;
  tomraDropsVolume?: number;
  beevisionDropsVolume?: number;
  amountDonated?: number;
  totalRevenue?: number;
  createdAt?: string;
  profileImage?: string;
  user_role?: number;
}

// The merge record itself holds no personal data. Identity is resolved
// server-side from the live User or from IdentityVault, and comes back as
// `identity`. When it is null the identity was erased on request.
interface MergeIdentity {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  source?: "user" | "vault";
}

interface MergeSnapshot {
  userRef: string;
  userId: string;
  identity?: MergeIdentity | null;
  balance?: number;
  droppies?: number;
  dropsVolume?: number;
  tomraDropsVolume?: number;
  beevisionDropsVolume?: number;
  amountDonated?: number;
  totalRevenue?: number;
  createdAt?: string;
}

interface MergeLog {
  _id: string;
  keptUserBefore: MergeSnapshot;
  mergedUserBefore: MergeSnapshot;
  keptUserAfter: MergeSnapshot;
  mergedBy?: { first_name?: string; last_name?: string; email?: string } | null;
  identityErasedAt?: string | null;
  createdAt: string;
}

// GET users/merge-preview: what merging would move and change, and why it would
// be refused. Nothing changes until the merge itself.
type PreviewAccount = MergeUser & {
  signIns: string[];
  emailVerified: boolean;
  phoneVerified: boolean;
};

interface MergePreview {
  keep: PreviewAccount;
  merge: PreviewAccount;
  moves: { label: string; count: number }[];
  after: PreviewAccount;
  notes: string[];
  refusals: string[];
}

const SIGN_IN_NAMES: Record<string, string> = {
  phone: "Phone",
  "google.com": "Google",
  "apple.com": "Apple",
  "old-link": "Google or Apple (old link)",
};

const MERGE_LOG_PAGE_SIZE = 20;

const formatEuro = (cents?: number) => {
  if (!Number.isFinite(cents ?? NaN)) return "€0.00";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(Number(cents ?? 0) / 100);
};

const fullName = (user: MergeUser | null) => {
  if (!user) return "";
  return (
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    "Unnamed user"
  );
};

const fullNameFromSnapshot = (snapshot?: MergeSnapshot | null) => {
  if (!snapshot) return "";
  if (!snapshot.identity) return "Identity erased";
  return (
    [snapshot.identity.first_name, snapshot.identity.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "Unnamed user"
  );
};

const contactFromSnapshot = (snapshot?: MergeSnapshot | null) => {
  if (!snapshot?.identity) return "-";
  return snapshot.identity.email ?? snapshot.identity.phone_number ?? "-";
};

function MergeAccountsScreen() {
  const { user: currentUser } = useAuth();
  const { setAlert } = useAlert();
  const { setIsLoading } = useAppLoading();

  // Two independent search panels. Each owns its own input, results, and
  // selected user. They never share state — the whole point of this screen is
  // to compare two different accounts side by side.
  const [keepSearch, setKeepSearch] = useState("");
  const [mergeSearch, setMergeSearch] = useState("");
  const [keepResults, setKeepResults] = useState<MergeUser[]>([]);
  const [mergeResults, setMergeResults] = useState<MergeUser[]>([]);
  const [keepSelected, setKeepSelected] = useState<MergeUser | null>(null);
  const [mergeSelected, setMergeSelected] = useState<MergeUser | null>(null);
  const [keepLoading, setKeepLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Past-merges history table — before/after snapshots, newest first.
  const [mergeLogs, setMergeLogs] = useState<MergeLog[]>([]);
  const [mergeLogsPage, setMergeLogsPage] = useState(1);
  const [mergeLogsHasNextPage, setMergeLogsHasNextPage] = useState(false);
  const [mergeLogsLoading, setMergeLogsLoading] = useState(false);
  const [mergeLogsSearch, setMergeLogsSearch] = useState("");

  const keepDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mergeDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mergeLogsDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMergeLogs = async (page: number, search: string) => {
    setMergeLogsLoading(true);
    try {
      const response = await axios.get(
        `users/merge-logs?page=${page}&limit=${MERGE_LOG_PAGE_SIZE}&search=${encodeURIComponent(
          search.trim()
        )}`
      );
      setMergeLogs(response?.data?.logs ?? []);
      setMergeLogsHasNextPage(Boolean(response?.data?.hasNextPage));
    } catch (error) {
      setMergeLogs([]);
      setMergeLogsHasNextPage(false);
    } finally {
      setMergeLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchMergeLogs(mergeLogsPage, mergeLogsSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeLogsPage]);

  // Art. 17 erasure for a closed account. Removes the stored name, email and
  // phone; the balances and the merge itself stay, because we keep those for
  // 7 years under Dutch tax law and they carry no personal data once the
  // identity is gone.
  const onEraseIdentity = async (log: MergeLog) => {
    const label =
      fullNameFromSnapshot(log.mergedUserBefore) ||
      log.mergedUserBefore?.userRef;
    const confirmText =
      `Erase the stored name, email and phone number of "${label}"?\n\n` +
      "The balances and the merge record itself stay, since we are required to keep " +
      "the financial record for 7 years. They will no longer carry a name.\n\n" +
      "This cannot be undone.";
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmText)) return;

    setIsLoading(true);
    try {
      await axios.delete(
        `users/stored-identity/${log.mergedUserBefore.userRef}`,
      );
      setAlert({
        isOpen: true,
        message: "Identity erased. The financial record was kept.",
        severity: "success",
      });
      fetchMergeLogs(mergeLogsPage, mergeLogsSearch);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        // axiosConfig rejects with the response itself.
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not erase the identity. Please check the logs.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Typing a new search resets to page 1. Debounced so we don't fire a
  // request per keystroke.
  useEffect(() => {
    if (mergeLogsDebounce.current) clearTimeout(mergeLogsDebounce.current);
    mergeLogsDebounce.current = setTimeout(() => {
      if (mergeLogsPage === 1) fetchMergeLogs(1, mergeLogsSearch);
      else setMergeLogsPage(1);
    }, 350);
    return () => {
      if (mergeLogsDebounce.current) clearTimeout(mergeLogsDebounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeLogsSearch]);

  const searchUsers = async (
    query: string,
    setResults: (rs: MergeUser[]) => void,
    setLoading: (b: boolean) => void
  ) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `users/all?page=1&limit=10&search=${encodeURIComponent(query.trim())}`
      );
      const list = (response?.data?.users ??
        response?.data ??
        []) as MergeUser[];
      setResults(Array.isArray(list) ? list : []);
    } catch (error) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keepDebounce.current) clearTimeout(keepDebounce.current);
    keepDebounce.current = setTimeout(() => {
      searchUsers(keepSearch, setKeepResults, setKeepLoading);
    }, 350);
    return () => {
      if (keepDebounce.current) clearTimeout(keepDebounce.current);
    };
  }, [keepSearch]);

  useEffect(() => {
    if (mergeDebounce.current) clearTimeout(mergeDebounce.current);
    mergeDebounce.current = setTimeout(() => {
      searchUsers(mergeSearch, setMergeResults, setMergeLoading);
    }, 350);
    return () => {
      if (mergeDebounce.current) clearTimeout(mergeDebounce.current);
    };
  }, [mergeSearch]);

  const fetchPreview = async (
    keepId: string,
    mergeId: string
  ): Promise<MergePreview | null> => {
    setPreviewLoading(true);
    try {
      const response = await axios.get(
        `users/merge-preview?keep=${keepId}&merge=${mergeId}`
      );
      // undefined: a copy of a request still running (axiosConfig); that one sets it.
      if (!response) return null;
      setPreview(response.data);
      return response.data;
    } catch (error: any) {
      setPreview(null);
      setAlert({
        isOpen: true,
        // axiosConfig rejects with the response itself.
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not check this merge.",
        severity: "error",
      });
      return null;
    } finally {
      setPreviewLoading(false);
    }
  };

  // Opened from Account Review with both accounts in the URL: select them.
  useEffect(() => {
    const keepId = searchParams.get("keep");
    const mergeId = searchParams.get("merge");
    if (!keepId || !mergeId || Number(currentUser?.user_role) !== 7) return;
    (async () => {
      const data = await fetchPreview(keepId, mergeId);
      if (!data) return;
      setKeepSelected(data.keep);
      setKeepSearch(fullName(data.keep) || data.keep.email || data.keep._id);
      setMergeSelected(data.merge);
      setMergeSearch(fullName(data.merge) || data.merge.email || data.merge._id);
    })();
  }, []);

  // Both sides picked: ask what the merge would do before it can be confirmed.
  useEffect(() => {
    if (
      !keepSelected?._id ||
      !mergeSelected?._id ||
      keepSelected._id === mergeSelected._id
    ) {
      setPreview(null);
      return;
    }
    if (
      preview?.keep._id === keepSelected._id &&
      preview?.merge._id === mergeSelected._id
    )
      return;
    setPreview(null);
    fetchPreview(keepSelected._id, mergeSelected._id);
  }, [keepSelected?._id, mergeSelected?._id]);

  const onSwap = () => {
    const [keep, merge] = [keepSelected, mergeSelected];
    const [keepText, mergeText] = [keepSearch, mergeSearch];
    setKeepSelected(merge);
    setMergeSelected(keep);
    setKeepSearch(mergeText);
    setMergeSearch(keepText);
  };

  const sameUserPicked = useMemo(
    () =>
      keepSelected?._id &&
      mergeSelected?._id &&
      keepSelected._id === mergeSelected._id,
    [keepSelected, mergeSelected]
  );

  const canMerge =
    Boolean(keepSelected?._id) &&
    Boolean(mergeSelected?._id) &&
    !sameUserPicked &&
    !isMerging &&
    preview?.keep._id === keepSelected?._id &&
    preview?.merge._id === mergeSelected?._id &&
    !preview?.refusals.length;

  const onMerge = async () => {
    if (!canMerge || !keepSelected || !mergeSelected) return;
    const confirmText =
      `Merge "${fullName(mergeSelected)}" (${mergeSelected.email ?? mergeSelected.phone_number ?? mergeSelected._id}) ` +
      `into "${fullName(keepSelected)}" (${keepSelected.email ?? keepSelected.phone_number ?? keepSelected._id})?\n\n` +
      "Everything on the merged account moves to the kept one in a single step, and the merged account is permanently deleted. This cannot be undone.";
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmText)) return;
    setIsMerging(true);
    setIsLoading(true);
    try {
      await axios.post("users/merge", {
        realUserId: keepSelected._id,
        fakeUserId: mergeSelected._id,
      });
      setAlert({
        isOpen: true,
        message: "Accounts merged. Everything moved to the kept account.",
        severity: "success",
      });
      // Reset state — the "merge-from" account no longer exists.
      setPreview(null);
      setMergeSelected(null);
      setMergeSearch("");
      setMergeResults([]);
      // The kept account now holds both balances and counters: show it as it is.
      const kept = await axios.get(`users/${keepSelected._id}`).catch(() => null);
      if (kept?.data?._id) setKeepSelected(kept.data);
      // Show the just-completed merge at the top of the history table.
      if (mergeLogsPage === 1) fetchMergeLogs(1, mergeLogsSearch);
      else setMergeLogsPage(1);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        // axiosConfig rejects with the response itself, so the backend's
        // reason (a scan in progress, ...) is in error.data.
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Merging failed. Please try again or check the logs.",
        severity: "error",
      });
    } finally {
      setIsMerging(false);
      setIsLoading(false);
    }
  };

  // Hard gate at component level too — even though the menu hides it and the
  // backend rejects non-super-admins, never render the UI for the wrong role.
  if (Number(currentUser?.user_role) !== 7) {
    return <Navigate to="/users" replace />;
  }

  return (
    <div>
      <div className="flex w-full justify-between items-start mb-8">
        <div>
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            Merge accounts
          </h1>
          <p className="text-sm text-gray-text mt-2 max-w-3xl">
            Use this tool when a single person has ended up with two accounts —
            for example, they signed up once via phone and again via Google.
            Pick the account to <strong>keep</strong> on the left and the
            duplicate to <strong>merge in</strong> on the right. Everything on
            the duplicate moves to the kept account in a single step (drops,
            payouts, balance, lottery tickets, punch cards, vouchers, Google/Apple
            sign-ins, and its phone number if the kept account has none), and the
            duplicate is permanently deleted. The check below shows what will
            happen before anything changes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <SidePanel
          title="Keep this account"
          subtitle="The kept account collects balances + history from the other one."
          accent="green"
          search={keepSearch}
          onSearchChange={(v) => {
            setKeepSearch(v);
            // Typing again means the previously-picked user no longer matches the input.
            // Drop the selection so fresh search results can render.
            if (keepSelected) setKeepSelected(null);
          }}
          results={keepResults}
          loading={keepLoading}
          selected={keepSelected}
          onSelect={(u) => {
            setKeepSelected(u);
            setKeepResults([]);
            setKeepSearch(fullName(u) || u.email || u._id);
          }}
          onClear={() => {
            setKeepSelected(null);
            setKeepSearch("");
            setKeepResults([]);
          }}
        />
        <SidePanel
          title="Merge this account into the kept one"
          subtitle="Everything on this account moves to the kept one. Then this account is deleted."
          accent="red"
          search={mergeSearch}
          onSearchChange={(v) => {
            setMergeSearch(v);
            if (mergeSelected) setMergeSelected(null);
          }}
          results={mergeResults}
          loading={mergeLoading}
          selected={mergeSelected}
          onSelect={(u) => {
            setMergeSelected(u);
            setMergeResults([]);
            setMergeSearch(fullName(u) || u.email || u._id);
          }}
          onClear={() => {
            setMergeSelected(null);
            setMergeSearch("");
            setMergeResults([]);
          }}
        />
      </div>

      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
        <h2 className="font-satoshi text-lg font-bold text-dark mb-4">
          Review &amp; confirm
        </h2>
        {keepSelected && mergeSelected && !sameUserPicked ? (
          <div className="space-y-3 text-sm font-satoshi text-dark">
            <p>
              You are about to merge <strong>{fullName(mergeSelected)}</strong>{" "}
              (
              {mergeSelected.email ??
                mergeSelected.phone_number ??
                "no contact"}
              ) into <strong>{fullName(keepSelected)}</strong> (
              {keepSelected.email ?? keepSelected.phone_number ?? "no contact"}
              ).
            </p>
            {previewLoading && !preview ? (
              <p className="text-gray-text">Checking what the merge would do...</p>
            ) : null}
            {preview?.refusals.length ? (
              <div className="rounded-md border border-red-base bg-red-50 px-4 py-3">
                <p className="font-medium text-red-base">
                  This merge can't be done here:
                </p>
                <ul className="list-disc pl-5 mt-1 text-red-base">
                  {preview.refusals.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {preview ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <p className="font-medium mb-2">Moves to the kept account</p>
                  {preview.moves.length ? (
                    <ul>
                      {preview.moves.map((m) => (
                        <li
                          key={m.label}
                          className="flex justify-between gap-4 border-b border-gray-border py-1.5"
                        >
                          <span>{m.label}</span>
                          <span className="font-medium">{m.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-text">No records to move.</p>
                  )}
                </div>
                <div>
                  <p className="font-medium mb-2">The kept account afterwards</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Stat label="Balance" value={formatEuro(preview.after.balance)} />
                    <Stat
                      label="Droppies"
                      value={String(preview.after.droppies ?? 0)}
                    />
                    <Stat
                      label="Phone"
                      value={`${preview.after.phone_number ?? "-"}${
                        preview.after.phone_number && preview.after.phoneVerified
                          ? " (verified)"
                          : ""
                      }`}
                    />
                    <Stat
                      label="Email verified"
                      value={preview.after.emailVerified ? "Yes" : "No"}
                    />
                    <Stat
                      label="Signs in with"
                      value={
                        preview.after.signIns
                          .map((x) => SIGN_IN_NAMES[x] ?? x)
                          .join(", ") || "-"
                      }
                    />
                  </div>
                </div>
              </div>
            ) : null}
            {preview?.notes.length ? (
              <ul className="list-disc pl-6 text-gray-text">
                {preview.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            ) : null}
            <p>
              The merged account is <strong>permanently deleted</strong>. This
              cannot be undone.
            </p>
          </div>
        ) : sameUserPicked ? (
          <p className="text-sm font-satoshi text-red-base">
            You picked the same user on both sides. Pick two different accounts
            to continue.
          </p>
        ) : (
          <p className="text-sm font-satoshi text-gray-text">
            Pick a user on each side to preview the merge.
          </p>
        )}

        <div className="flex flex-row items-center justify-end mt-6 gap-3">
          <SecondaryButton
            onClick={() => {
              setKeepSelected(null);
              setMergeSelected(null);
              setKeepSearch("");
              setMergeSearch("");
              setKeepResults([]);
              setMergeResults([]);
            }}
            label="Reset"
          />
          {keepSelected && mergeSelected && !sameUserPicked ? (
            <SecondaryButton onClick={onSwap} label="Swap sides" />
          ) : null}
          <PrimaryButton
            label={isMerging ? "Merging…" : "Merge accounts"}
            disabled={!canMerge}
            onClick={onMerge}
          />
        </div>
      </div>

      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none mt-6">
        <h2 className="font-satoshi text-lg font-bold text-dark mb-1">
          Merge history
        </h2>
        <p className="text-sm text-gray-text mb-4">
          Past merges, newest first. Shows each account's state right before the
          merge and the kept account's state right after.
        </p>

        <div className="w-[550px] mb-4">
          <SearchInput
            value={mergeLogsSearch}
            onChange={(e) => setMergeLogsSearch(e.target.value)}
            placeholder="Search by name, email, or phone number (e.g. +31612345678)"
            hideIcon
          />
        </div>

        {mergeLogsLoading && !mergeLogs.length ? (
          <p className="text-sm text-gray-text">Loading…</p>
        ) : !mergeLogs.length ? (
          <p className="text-sm text-gray-text">
            {mergeLogsSearch.trim()
              ? "No merges match this search."
              : "No merges yet."}
          </p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
              <thead className="font-medium bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">Date</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">Kept account</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">
                    Merged-in account (deleted)
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">
                    Balance (kept account)
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">
                    Balance (merged-in account)
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">
                    Balance after merge
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">Merged by</th>
                  <th
                    className="px-2 sm:px-4 py-2 sm:py-3 text-right"
                    title="Art. 17 erasure request from the merged-in account holder"
                  >
                    Erasure
                  </th>
                </tr>
              </thead>
              <tbody>
                {mergeLogs.map((log) => (
                  <tr
                    key={log._id}
                    className="bg-white hover:bg-gray-100 transition ease-linear border-b"
                  >
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      {log.createdAt
                        ? dayjs(log.createdAt).format("DD/MM/YYYY HH:mm")
                        : "-"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <span className="block font-medium">
                        {fullNameFromSnapshot(log.keptUserBefore)}
                      </span>
                      <span className="block text-xs text-gray-text break-all">
                        {contactFromSnapshot(log.keptUserBefore)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <span className="block font-medium">
                        {fullNameFromSnapshot(log.mergedUserBefore)}
                      </span>
                      <span className="block text-xs text-gray-text break-all">
                        {contactFromSnapshot(log.mergedUserBefore)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      {formatEuro(log.keptUserBefore?.balance)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      {formatEuro(log.mergedUserBefore?.balance)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap font-medium">
                      {formatEuro(log.keptUserAfter?.balance)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      {log.mergedBy
                        ? [log.mergedBy.first_name, log.mergedBy.last_name]
                            .filter(Boolean)
                            .join(" ")
                            .trim() ||
                          log.mergedBy.email ||
                          "-"
                        : "-"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-right whitespace-nowrap">
                      {log.identityErasedAt ? (
                        <span className="text-xs text-gray-text">
                          Erased{" "}
                          {dayjs(log.identityErasedAt).format("DD/MM/YYYY")}
                        </span>
                      ) : log.mergedUserBefore?.identity ? (
                        <PrimaryButton
                          label="Erase identity"
                          size="sm"
                          onClick={() => onEraseIdentity(log)}
                          className="!bg-white !border-red-base hover:!bg-red-base hover:!opacity-100 group"
                          textClassName="!text-red-base group-hover:!text-white"
                        />
                      ) : (
                        <span className="text-xs text-gray-text">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">Page {mergeLogsPage}</p>
          <div className="flex gap-2">
            <PrimaryButton
              label="Previous"
              size="sm"
              onClick={() => setMergeLogsPage((p) => Math.max(1, p - 1))}
              disabled={mergeLogsPage <= 1 || mergeLogsLoading}
              className="!bg-white !text-dark !border-gray-border disabled:opacity-40"
            />
            <PrimaryButton
              label="Next"
              size="sm"
              onClick={() => setMergeLogsPage((p) => p + 1)}
              disabled={!mergeLogsHasNextPage || mergeLogsLoading}
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark disabled:opacity-40"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface SidePanelProps {
  title: string;
  subtitle: string;
  accent: "green" | "red";
  search: string;
  onSearchChange: (v: string) => void;
  results: MergeUser[];
  loading: boolean;
  selected: MergeUser | null;
  onSelect: (u: MergeUser) => void;
  onClear: () => void;
}

function SidePanel({
  title,
  subtitle,
  accent,
  search,
  onSearchChange,
  results,
  loading,
  selected,
  onSelect,
  onClear,
}: SidePanelProps) {
  const accentClass =
    accent === "green"
      ? "border-l-4 border-l-green-500"
      : "border-l-4 border-l-red-500";

  return (
    <div
      className={`w-full bg-white rounded-xl border border-gray-border drop-shadow-none ${accentClass}`}
    >
      <div className="px-6 py-4 border-b border-gray-border">
        <p className="font-satoshi font-bold text-base text-dark">{title}</p>
        <p className="text-xs text-gray-text mt-1">{subtitle}</p>
      </div>
      <div className="px-6 py-4">
        <SearchInput
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by name, email, phone, or id…"
          hideIcon
        />
        {loading ? (
          <p className="text-xs text-gray-text mt-2">Searching…</p>
        ) : null}
        {!selected && results.length > 0 ? (
          <ul className="mt-3 max-h-56 overflow-y-auto divide-y divide-gray-border border border-gray-border rounded-md">
            {results.map((u) => (
              <li key={u._id}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 font-satoshi text-sm"
                  onClick={() => onSelect(u)}
                >
                  <span className="block font-medium text-dark">
                    {fullName(u)}
                  </span>
                  <span className="block text-xs text-gray-text">
                    {u.email ?? u.phone_number ?? u._id}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {selected ? (
          <div className="mt-4 rounded-md border border-gray-border p-4 bg-gray-50">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-satoshi font-bold text-base text-dark truncate">
                  {fullName(selected)}
                </p>
                <p className="text-xs text-gray-text break-all">
                  {selected.email ?? "—"}
                  {selected.phone_number ? ` · ${selected.phone_number}` : ""}
                </p>
                <p className="text-[10px] text-gray-text mt-1 break-all">
                  ID: {selected._id}
                </p>
              </div>
              <button
                type="button"
                className="text-xs text-red-base font-medium"
                onClick={onClear}
              >
                Clear
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs font-satoshi text-dark">
              <Stat label="Balance" value={formatEuro(selected.balance)} />
              <Stat label="Droppies" value={String(selected.droppies ?? 0)} />
              <Stat
                label="Drops volume"
                value={String(selected.dropsVolume ?? 0)}
              />
              <Stat
                label="TOMRA volume"
                value={String(selected.tomraDropsVolume ?? 0)}
              />
              <Stat
                label="Beevision volume"
                value={String(selected.beevisionDropsVolume ?? 0)}
              />
              <Stat
                label="Donated"
                value={formatEuro(selected.amountDonated)}
              />
              <Stat
                label="Joined"
                value={
                  selected.createdAt
                    ? dayjs(selected.createdAt).format("DD-MM-YYYY")
                    : "—"
                }
              />
              <Stat label="Role" value={String(selected.user_role ?? "—")} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-gray-text">
        {label}
      </span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export default withProtectedRoute(MergeAccountsScreen);
